import React, { useState, useEffect, useMemo, useRef } from "react";
import { X, Check, ChevronLeft, ChevronRight, Loader2, AlertCircle } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TenantStatusBadge, TenantPackageBadge } from "./TenantBadges";
import { slugify, validateSlug } from "./slug";

const STEPS = [
  { id: 1, key: "info", label: "Tenant Info" },
  { id: 2, key: "owner", label: "Owner" },
  { id: 3, key: "package", label: "Package" },
  { id: 4, key: "review", label: "Review" },
];

const PACKAGE_META = {
  STARTER: {
    title: "Starter",
    desc: "Untuk toko kecil / pilot project.",
    perks: ["1 domain", "Fitur dasar", "Support komunitas"],
  },
  GROWTH: {
    title: "Growth",
    desc: "Untuk toko yang mulai berkembang.",
    perks: ["Custom domain", "Semua fitur", "Support prioritas"],
  },
  ENTERPRISE: {
    title: "Enterprise",
    desc: "Untuk brand & multi-cabang.",
    perks: ["Multi-domain", "Custom integrasi", "Dedicated support"],
  },
};

function StepIndicator({ current }) {
  return (
    <ol className="flex items-center gap-2 mb-6" data-testid="wizard-progress">
      {STEPS.map((s, idx) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <li key={s.id} className="flex items-center gap-2 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <div
                data-testid={`wizard-step-indicator-${s.id}`}
                className={`w-7 h-7 shrink-0 rounded-full grid place-items-center text-[11px] font-black border transition-all ${
                  done
                    ? "bg-emerald-500 border-emerald-500 text-white"
                    : active
                    ? "border-white text-white bg-white/10"
                    : "border-white/20 text-white/40"
                }`}
              >
                {done ? <Check size={13} /> : s.id}
              </div>
              <span
                className={`text-[11px] font-bold uppercase tracking-wider truncate ${
                  active ? "text-white" : "text-white/40"
                }`}
              >
                {s.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`h-px flex-1 ${done ? "bg-emerald-500/70" : "bg-white/10"}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function Field({ label, hint, error, children, testId }) {
  return (
    <div className="mb-4" data-testid={testId}>
      <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">
        {label}
      </label>
      {children}
      {hint && !error && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
      {error && (
        <div className="text-[11px] text-rose-400 mt-1 flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </div>
      )}
    </div>
  );
}

const DEFAULT_FORM = {
  tenant_name: "",
  tenant_slug: "",
  tenant_slug_edited: false,
  tenant_status: "ACTIVE",
  package: "STARTER",
  owner_name: "",
  owner_email: "",
  owner_phone: "",
  notes: "",
};

export default function CreateTenantWizard({ open, onClose, onCreated, enums }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [slugCheck, setSlugCheck] = useState({ checking: false, available: null, error: null });
  const slugTimer = useRef(null);

  // Reset saat dibuka
  useEffect(() => {
    if (open) {
      setStep(1);
      setForm(DEFAULT_FORM);
      setSlugCheck({ checking: false, available: null, error: null });
    }
  }, [open]);

  // Auto slug dari name kalau user belum edit slug manual
  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!form.tenant_slug_edited) {
      setForm((f) => ({ ...f, tenant_slug: slugify(f.tenant_name) }));
    }
  }, [form.tenant_name, form.tenant_slug_edited]);

  // Live check slug uniqueness (debounced)
  useEffect(() => {
    if (!open) return;
    if (slugTimer.current) clearTimeout(slugTimer.current);
    const slug = form.tenant_slug;
    const localErr = validateSlug(slug);
    if (localErr) {
      setSlugCheck({ checking: false, available: false, error: localErr });
      return;
    }
    setSlugCheck({ checking: true, available: null, error: null });
    slugTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/platform/tenants/check-slug`, { params: { slug } });
        setSlugCheck({ checking: false, available: r.data.available, error: r.data.error });
      } catch (e) {
        setSlugCheck({ checking: false, available: null, error: "Gagal cek slug" });
      }
    }, 400);
    return () => slugTimer.current && clearTimeout(slugTimer.current);
  }, [form.tenant_slug, open]);

  const stepValid = useMemo(() => {
    if (step === 1) {
      return (
        form.tenant_name.trim().length > 0 &&
        !validateSlug(form.tenant_slug) &&
        slugCheck.available === true
      );
    }
    if (step === 2) {
      // Owner email optional but must be valid kalau diisi
      if (form.owner_email && !/^\S+@\S+\.\S+$/.test(form.owner_email)) return false;
      return true;
    }
    if (step === 3) {
      return !!form.package;
    }
    return true;
  }, [step, form, slugCheck]);

  const goNext = () => {
    if (!stepValid) return;
    setStep((s) => Math.min(STEPS.length, s + 1));
  };
  const goPrev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        tenant_name: form.tenant_name.trim(),
        tenant_slug: form.tenant_slug.trim(),
        tenant_status: form.tenant_status,
        package: form.package,
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim(),
        owner_phone: form.owner_phone.trim(),
        notes: form.notes.trim(),
      };
      const r = await api.post("/platform/tenants", payload);
      toast.success(`Tenant "${r.data.tenant.tenant_name}" berhasil dibuat (${r.data.tenant.tenant_code})`);
      onCreated?.(r.data.tenant);
      onClose?.();
    } catch (e) {
      notifyError(e, "Gagal membuat tenant");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const statuses = enums?.statuses || ["ACTIVE", "INACTIVE", "SUSPENDED", "ARCHIVED"];
  const packages = enums?.packages || ["STARTER", "GROWTH", "ENTERPRISE"];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      data-testid="tenant-wizard-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className="w-full max-w-2xl bg-[#0F0F17] border border-white/10 rounded-3xl shadow-2xl"
        data-testid="tenant-wizard"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div>
            <div className="text-white font-display font-black text-lg">Buat Tenant Baru</div>
            <div className="text-[11px] text-white/40">
              Step {step} dari {STEPS.length}
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="wizard-close"
            aria-label="Tutup"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6">
          <StepIndicator current={step} />

          {step === 1 && (
            <div data-testid="wizard-step-1">
              <Field label="Nama Tenant *" testId="field-tenant-name">
                <Input
                  autoFocus
                  value={form.tenant_name}
                  onChange={(e) => setField({ tenant_name: e.target.value })}
                  placeholder="Contoh: Toko Maju Jaya"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="input-tenant-name"
                />
              </Field>

              <Field
                label="Slug *"
                hint="Auto-generated dari nama. Bisa diubah manual — hanya lowercase, angka, dan dash."
                error={slugCheck.error}
                testId="field-tenant-slug"
              >
                <div className="relative">
                  <Input
                    value={form.tenant_slug}
                    onChange={(e) =>
                      setField({ tenant_slug: e.target.value.toLowerCase(), tenant_slug_edited: true })
                    }
                    placeholder="toko-maju-jaya"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pr-9 font-mono"
                    data-testid="input-tenant-slug"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    {slugCheck.checking ? (
                      <Loader2 size={14} className="text-white/40 animate-spin" />
                    ) : slugCheck.available === true ? (
                      <Check size={14} className="text-emerald-400" data-testid="slug-available-icon" />
                    ) : slugCheck.available === false ? (
                      <X size={14} className="text-rose-400" />
                    ) : null}
                  </div>
                </div>
                {form.tenant_slug && (
                  <div className="text-[11px] text-white/40 mt-1">
                    Preview:{" "}
                    <span className="font-mono text-white/70" data-testid="slug-preview">
                      {form.tenant_slug}
                    </span>
                  </div>
                )}
              </Field>

              <Field label="Status" testId="field-tenant-status">
                <div className="flex flex-wrap gap-2">
                  {statuses.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setField({ tenant_status: s })}
                      className={`px-3 py-1.5 rounded-full border text-[11px] font-black uppercase tracking-wider transition-colors ${
                        form.tenant_status === s
                          ? "bg-white text-black border-white"
                          : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
                      }`}
                      data-testid={`status-option-${s.toLowerCase()}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
          )}

          {step === 2 && (
            <div data-testid="wizard-step-2">
              <Field label="Nama Owner" testId="field-owner-name">
                <Input
                  value={form.owner_name}
                  onChange={(e) => setField({ owner_name: e.target.value })}
                  placeholder="Nama lengkap owner tenant"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="input-owner-name"
                />
              </Field>
              <Field
                label="Email Owner"
                hint="Optional. Format email standar."
                error={
                  form.owner_email && !/^\S+@\S+\.\S+$/.test(form.owner_email)
                    ? "Format email tidak valid."
                    : null
                }
                testId="field-owner-email"
              >
                <Input
                  type="email"
                  value={form.owner_email}
                  onChange={(e) => setField({ owner_email: e.target.value })}
                  placeholder="owner@tenant.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="input-owner-email"
                />
              </Field>
              <Field label="Nomor WhatsApp / HP" testId="field-owner-phone">
                <Input
                  value={form.owner_phone}
                  onChange={(e) => setField({ owner_phone: e.target.value })}
                  placeholder="081234567890"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="input-owner-phone"
                />
              </Field>
              <Field label="Catatan (Internal)" testId="field-notes">
                <Textarea
                  value={form.notes}
                  onChange={(e) => setField({ notes: e.target.value })}
                  placeholder="Catatan internal untuk platform owner."
                  rows={3}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="input-notes"
                />
              </Field>
            </div>
          )}

          {step === 3 && (
            <div data-testid="wizard-step-3" className="grid gap-3 md:grid-cols-3">
              {packages.map((p) => {
                const meta = PACKAGE_META[p] || { title: p, desc: "", perks: [] };
                const selected = form.package === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setField({ package: p })}
                    className={`text-left p-4 rounded-2xl border transition-all ${
                      selected
                        ? "border-white bg-white/10"
                        : "border-white/10 bg-white/5 hover:border-white/30"
                    }`}
                    data-testid={`package-option-${p.toLowerCase()}`}
                  >
                    <TenantPackageBadge package={p} size="sm" />
                    <div className="text-white font-display font-black text-base mt-2">
                      {meta.title}
                    </div>
                    <div className="text-[11px] text-white/50 mt-1 mb-3">{meta.desc}</div>
                    <ul className="space-y-1">
                      {meta.perks.map((perk, i) => (
                        <li key={i} className="flex items-center gap-1.5 text-[11px] text-white/70">
                          <Check size={11} className="text-emerald-400 shrink-0" /> {perk}
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>
          )}

          {step === 4 && (
            <div data-testid="wizard-step-4">
              <div className="text-[11px] text-white/40 uppercase tracking-wider mb-3">
                Review Tenant sebelum simpan
              </div>
              <div className="rounded-2xl border border-white/10 divide-y divide-white/5">
                <ReviewRow label="Nama Tenant" value={form.tenant_name} testId="review-tenant-name" />
                <ReviewRow
                  label="Slug"
                  value={<span className="font-mono">{form.tenant_slug}</span>}
                  testId="review-tenant-slug"
                />
                <ReviewRow
                  label="Status"
                  value={<TenantStatusBadge status={form.tenant_status} size="sm" />}
                  testId="review-status"
                />
                <ReviewRow
                  label="Package"
                  value={<TenantPackageBadge package={form.package} size="sm" />}
                  testId="review-package"
                />
                <ReviewRow label="Owner" value={form.owner_name || "—"} testId="review-owner-name" />
                <ReviewRow label="Email" value={form.owner_email || "—"} testId="review-owner-email" />
                <ReviewRow label="HP" value={form.owner_phone || "—"} testId="review-owner-phone" />
                {form.notes && <ReviewRow label="Catatan" value={form.notes} testId="review-notes" />}
              </div>
              <div className="text-[11px] text-white/40 mt-3">
                <strong className="text-white/70">tenant_id (UUID)</strong> dan{" "}
                <strong className="text-white/70">tenant_code (TEN-XXXXXX)</strong> akan di-generate
                otomatis oleh backend dan tidak bisa diubah setelah dibuat.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
            data-testid="wizard-cancel"
          >
            Batal
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={goPrev}
                className="border-white/10 bg-transparent text-white hover:bg-white/10"
                data-testid="wizard-prev"
              >
                <ChevronLeft size={14} className="mr-1" /> Kembali
              </Button>
            )}
            {step < STEPS.length ? (
              <Button
                onClick={goNext}
                disabled={!stepValid}
                className="bg-white text-black hover:bg-white/90 disabled:opacity-40"
                data-testid="wizard-next"
              >
                Lanjut <ChevronRight size={14} className="ml-1" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40"
                data-testid="wizard-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="mr-1 animate-spin" /> Menyimpan…
                  </>
                ) : (
                  <>
                    <Check size={14} className="mr-1" /> Buat Tenant
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value, testId }) {
  return (
    <div className="grid grid-cols-3 gap-3 px-4 py-3" data-testid={testId}>
      <div className="text-[11px] text-white/40 uppercase tracking-wider self-center">{label}</div>
      <div className="col-span-2 text-sm text-white/90 break-words">{value || <span className="text-white/30">—</span>}</div>
    </div>
  );
}
